/**
 * The `UOM` master — one of the contract's 33 doctypes (`docs/bosun-frappe-contract.md` §3.1),
 * read through `/api/resource/UOM` like every other master picker in `useEnquiryFromMail.ts`.
 *
 * Small (39 rows on the live bench) and barely changes within a session, so a long `staleTime`
 * is deliberate — a UOM resolver that opens this list on every unresolved line would otherwise
 * re-fetch 39 rows on every open, wasted traffic for a master this static. (No screen renders such
 * a resolver today — see `useMailExtraction.ts`'s `verifyUoms`, which shares this same cache
 * through `fetchUomMaster` below without a UI on top of it.)
 *
 * Kept separate from `useEnquiryFromMail.ts`'s generic `useMasterPicker` (which is a SEARCH —
 * `filters` narrow server-side, one page at a time): resolving an extracted UOM needs the WHOLE
 * list at once, to check a claim against it and to offer the closed set of legal values in a
 * `<select>`, not to search-as-you-type through it.
 */

import { useQuery, type QueryClient } from '@tanstack/react-query'
import { getResource } from './frappe'
import { str } from './mappers/coerce'

/** Comfortably above the live bench's 39 rows — this is a master list, not a paginated one. */
const UOM_MASTER_LIMIT = 200

/**
 * The query, as options, so the hook below and NON-hook callers share one cache entry.
 *
 * `useMailExtraction.ts`'s `verifyUoms` used to issue its own bare `getResource('UOM', …)` on
 * every extraction — the same ~39 rows, re-fetched each run, while this hook already had them.
 * It now reads through `fetchUomMaster` and the two cannot drift apart.
 */
export const uomMasterQuery = {
  queryKey: ['uom-master'] as const,
  queryFn: async () => {
    const page = await getResource<Record<string, unknown>>('UOM', {
      fields: ['name'],
      limit: UOM_MASTER_LIMIT,
      orderBy: 'name asc',
    })
    return page.data.map((row) => str(row.name)).filter((name) => name !== '')
  },
  staleTime: 30 * 60_000,
}

export function useUomMaster() {
  return useQuery(uomMasterQuery)
}

/** The same master list, for code that isn't a component. Resolves from cache when it's warm. */
export function fetchUomMaster(queryClient: QueryClient): Promise<string[]> {
  return queryClient.fetchQuery(uomMasterQuery)
}
